import { ToolkitStore } from '@reduxjs/toolkit/dist/configureStore'
import WalletRepository, { Bank, Wallet, WALLET_TYPE } from '@/repositories/wallet-repository'
import { RootState } from '@/store'
import { WalletsActions as WalletActions } from '@/store/wallets-reducer'

export interface WalletStyle {
    background: string
    filter?: string
    opacity?: number
    mixBlendMode?: string
    walletLogo: string
}

type WalletStyleMap = {
    [provider: string]: (data: { providerData?: any; defaultLogo: string; version: 'v1' | 'v2' }) => WalletStyle
}

export const PAYMENT_TYPE_NAMES = {
    paySmallSmall: 'Pay Small Small',
    bank: 'Cash At Bank',
} as const

export const PROVIDER_NAME_MAP = {
    vodafone: 'TELECEL',
} as const

export default class WalletService {
    constructor(
        private walletRepository: WalletRepository,
        private store: ToolkitStore<RootState>,
        private walletsActions: WalletActions,
    ) {}

    getAllBanks() {
        return this.walletRepository.getAllBanks()
    }

    async getAllWallets() {
        const [walletsResponse, ecgWalletResponse] = await Promise.all([
            this.walletRepository.getAllWallets(),
            this.walletRepository.getECGWallet(),
        ])
        const ecgWallet = ecgWalletResponse.data
        const otherWallet = walletsResponse.data.filter(wallet => wallet.providerName !== 'ECG')

        const wallets = this.sortWalletTypes([...otherWallet, ecgWallet])
        walletsResponse.data = wallets

        this.store.dispatch(this.walletsActions.setWallets(wallets ?? []))

        await this.getMomoProviders()

        return wallets
    }

    addMomoWallet(accountNumber: string, providerName: string) {
        return this.walletRepository.addMomoWallets({
            accountNumber,
            providerName,
        })
    }

    verifyBankAccountNumber({
        selectedWalletId,
        accountNumber,
        bank,
    }: {
        selectedWalletId: string
        accountNumber: string
        bank: Bank
    }) {
        return this.walletRepository.verifyBankAccountNumber({
            selectedWalletId,
            accountNumber,
            bankCode: bank.bankCode,
        })
    }

    addBankCard(cardNumber: string) {
        return this.walletRepository.addBankCard({
            cardNumber,
        })
    }

    removeWallet(wallet: Wallet) {
        return this.walletRepository.deleteWallet(wallet.id)
    }

    verifyAddMomoWallet(paylaod: { otpCode: string; otpPrefix: string; requestId: string }) {
        return this.walletRepository.verifyAddMomoWallets(paylaod)
    }

    isECGWallet(wallet?: Wallet | null) {
        return wallet?.providerName === 'ECG'
    }

    isGMoneyWallet(wallet: Wallet) {
        return wallet.providerName?.toUpperCase() === 'G-MONEY'
    }

    isBankCard(wallet?: Wallet | null) {
        return !!wallet?.cardBinData && wallet.walletType === WALLET_TYPE.CARD
    }

    isPaySmallSmallCard(wallet: Wallet) {
        return wallet.providerName == 'pay-small-small'
    }

    isMoMoWallet(wallet: Wallet) {
        return ['mtn', 'vodafone', 'airteltigo'].includes(wallet.providerName?.toLowerCase() ?? '')
    }

    getWalletProviderName = ({
        wallet,
        isPayingSmallSmall,
        isBankPayment,
    }: {
        wallet: Wallet | null
        isPayingSmallSmall?: boolean
        isBankPayment?: boolean
    }) => {
        let paymentType: keyof typeof PAYMENT_TYPE_NAMES | '' = ''
        if (isPayingSmallSmall) paymentType = 'paySmallSmall'
        else if (isBankPayment) paymentType = 'bank'

        if (paymentType) {
            return PAYMENT_TYPE_NAMES[paymentType]
        }

        // Wallet provider lookup
        const providerKey = wallet?.providerName?.toLowerCase()
        const mappedProvider = providerKey ? PROVIDER_NAME_MAP[providerKey as keyof typeof PROVIDER_NAME_MAP] : null

        return mappedProvider ?? wallet?.providerName?.toUpperCase() ?? wallet?.walletType ?? ''
    }

    withdrawFromECGWallet(payload: { amount: string | number; destinationWalletId: string }) {
        return this.walletRepository.withdrawPowerAppBalance(payload)
    }

    private providerStyles: WalletStyleMap = {
        ecg: ({ defaultLogo, version }) => ({
            background: version === 'v1' ? '#FEFEFE' : '#2E388F',
            opacity: version === 'v1' ? 0.2 : 1,
            walletLogo: '/images/logo.png',
        }),

        mtn: ({ defaultLogo, version }) => ({
            background: version === 'v1' ? '#FBB520' : '#004F72',
            opacity: version === 'v1' ? 0.2 : 1,
            walletLogo: defaultLogo,
        }),

        vodafone: ({ providerData, defaultLogo, version }) => ({
            background: 'linear-gradient(45deg, #FE0000, #720000)',
            opacity: version === 'v1' ? 0.2 : 1,
            walletLogo: providerData?.alternativeLogoUrl ?? defaultLogo,
        }),

        'g-money': ({ defaultLogo, version }) => ({
            background: '#3B3B3B',
            opacity: version === 'v1' ? 0.2 : 1,
            walletLogo: defaultLogo,
        }),

        'ghana.gov': ({ defaultLogo, version }) => ({
            background: '#FEFEFE',
            opacity: version === 'v1' ? 0.2 : 1,
            walletLogo: '/images/logo-pay-bankpay.png',
        }),
    }

    private getDefaultStyle(version: 'v1' | 'v2'): WalletStyle {
        return {
            background: '#FEFEFE',
            opacity: version === 'v1' ? 0.2 : 1,
            walletLogo: '/images/logo.png',
        }
    }

    getWalletStyles(wallet: Wallet, version: 'v1' | 'v2' = 'v1'): WalletStyle {
        const provider = wallet.providerName?.toLowerCase()
        const momoProviders = this.store.getState().wallets.momoProviders
        const providerData = momoProviders.find(p => p.alias === provider)

        // Set up default style
        const defaultStyle = this.getDefaultStyle(version)
        let style = { ...defaultStyle, walletLogo: providerData?.logoUrl ?? defaultStyle.walletLogo }

        const bankCardStyles = this.getBankCardStyles({ wallet, style })
        if (bankCardStyles) {
            return bankCardStyles
        }

        // Check for specific provider styling
        if (provider && this.providerStyles[provider]) {
            return this.providerStyles[provider]({
                providerData,
                defaultLogo: style.walletLogo,
                version,
            })
        }

        if (this.isPaySmallSmallCard(wallet)) {
            return {
                ...style,
                opacity: 1,
                background: '#0E479D',
                walletLogo: '/images/albrim-mf.png',
            }
        }

        return style
    }

    getWalletStylesV1(wallet: Wallet): WalletStyle {
        return this.getWalletStyles(wallet, 'v1')
    }

    getWalletStylesV2(wallet: Wallet): WalletStyle {
        return this.getWalletStyles(wallet, 'v2')
    }

    getBankCardStyles({ wallet, style }: { wallet: Wallet; style: WalletStyle }) {
        if (this.isBankCard(wallet)) {
            const cardType = wallet.cardBinData?.cardType?.toUpperCase()

            if (wallet.cardBinData?.bankLogoUrl) {
                return { ...style, walletLogo: wallet.cardBinData?.bankLogoUrl }
            }
            if (cardType === 'VISA' || wallet.accountNumber?.startsWith('4')) {
                return { ...style, walletLogo: '/images/visa.png' }
            }

            if (wallet.accountNumber?.startsWith('5')) {
                return {
                    ...style,
                    walletLogo: '/images/master-card.png',
                }
            }

            return {
                ...style,
                walletLogo: '/images/logo.png',
            }
        }
    }

    async getMomoProviders() {
        const res = await this.walletRepository.getMomoProviders()
        this.store.dispatch(this.walletsActions.setMomoProviders(res?.data ?? []))
        return res.data
    }

    sortWalletTypes(wallets: Wallet[]): Wallet[] {
        const walletTypeOrder: Wallet['walletType'][] = ['mobilemoney', 'card', 'fineract']

        // Sort the list based on the custom order
        return [...wallets].sort(
            (a, b) => walletTypeOrder.indexOf(a.walletType) - walletTypeOrder.indexOf(b.walletType),
        )
    }
}
